import { useHotkeys } from '@mantine/hooks';
import { useModals } from '@mantine/modals';

import { useNavigate } from '@tanstack/react-router';

interface UseKeyboardShortcutsOptions {
  onSearchFocus?: () => void;
  onViewSwitch?: (view: 'projects' | 'resources') => void;
  onCalendarNavigate?: (direction: 'left' | 'right') => void;
  onCalendarZoom?: (direction: 'in' | 'out') => void;
  onCreateProject?: () => void;
}

export function useKeyboardShortcuts(options: UseKeyboardShortcutsOptions = {}) {
  const navigate = useNavigate();
  const modals = useModals();

  const { onSearchFocus, onViewSwitch, onCalendarNavigate, onCalendarZoom, onCreateProject } =
    options;

  // Global shortcuts
  useHotkeys([
    // Navigation shortcuts
    ['mod+p', () => navigate({ to: '/app/projects' })],
    ['mod+l', () => navigate({ to: '/app/planning' })],

    // Create new project
    [
      'mod+n',
      (event) => {
        event.preventDefault();
        if (onCreateProject) {
          onCreateProject();
        }
      },
    ],

    // Search focus
    [
      'mod+f',
      (event) => {
        event.preventDefault();
        onSearchFocus?.();
      },
    ],

    // View switching (planning page)
    [
      'mod+1',
      (event) => {
        event.preventDefault();
        onViewSwitch?.('projects');
      },
    ],
    [
      'mod+2',
      (event) => {
        event.preventDefault();
        onViewSwitch?.('resources');
      },
    ],

    // Calendar navigation (planning page)
    [
      'mod+ArrowLeft',
      (event) => {
        event.preventDefault();
        onCalendarNavigate?.('left');
      },
    ],
    [
      'mod+ArrowRight',
      (event) => {
        event.preventDefault();
        onCalendarNavigate?.('right');
      },
    ],

    // Calendar zoom (planning page)
    [
      'mod+Equal',
      (event) => {
        event.preventDefault();
        onCalendarZoom?.('in');
      },
    ],
    [
      'mod+Minus',
      (event) => {
        event.preventDefault();
        onCalendarZoom?.('out');
      },
    ],

    // Close modals/clear search
    [
      'Escape',
      () => {
        modals.closeAll();
      },
    ],
  ]);

  return {
    shortcuts: {
      navigation: [
        { key: 'Ctrl/⌘+P', description: 'Gå til Projekter' },
        { key: 'Ctrl/⌘+L', description: 'Gå til Planlægning' },
        { key: 'Ctrl/⌘+K', description: 'Åbn Finder' },
      ],
      actions: [
        { key: 'Ctrl/⌘+N', description: 'Opret nyt projekt' },
        { key: 'Ctrl/⌘+F', description: 'Fokusér søgefelt' },
        { key: 'Escape', description: 'Luk modaler' },
      ],
      planning: [
        { key: 'Ctrl/⌘+1', description: 'Skift til Projekter visning' },
        { key: 'Ctrl/⌘+2', description: 'Skift til Ressourcer visning' },
        { key: 'Ctrl/⌘+←/→', description: 'Navigér kalender' },
        { key: 'Ctrl/⌘+±', description: 'Zoom kalender' },
      ],
    },
  };
}
